<template>
  <v-dialog
    :value="open"
    persistent
    max-width="34rem"
    content-class="generate-invoice-dialog"
  >
    <v-card class="generate-invoice-card">
      <v-toolbar flat>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon title="Close" @click.prevent="$emit('cancel')">
          <v-icon class="heading grey--text text--darken-4">close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-progress-linear
        v-if="loading"
        indeterminate
        height="3"
        class="generate-invoice-progress"
      ></v-progress-linear>

      <v-card-text>
        <!-- A caller can put a step in front of the bank choice (for
             example asking for the credit duration first). While that
             step is shown, the bank choice and the actions stay hidden. -->
        <slot v-if="$slots.prompt" name="prompt"></slot>

        <template v-else>
          <p class="generate-invoice-lead">
            Are you sure you want to generate the invoice number?
          </p>
          <p class="generate-invoice-hint">
            Choose the bank account to print on this invoice.
          </p>

          <label class="generate-invoice-label" :for="fieldId">
            Bank account
          </label>
          <v-autocomplete
            :id="fieldId"
            :value="bank"
            :items="banks"
            item-text="text"
            item-value="value"
            placeholder="Choose bank"
            class="generate-invoice-field"
            prepend-inner-icon="account_balance"
            single-line
            hide-details
            @change="select"
          />

          <div v-if="selected" class="generate-invoice-bank">
            <dl class="generate-invoice-details">
              <div
                v-for="row in details"
                :key="row.label"
                class="generate-invoice-detail"
                :class="{ 'generate-invoice-detail--wide': row.wide }"
              >
                <dt>{{ row.label }}</dt>
                <dd :class="{ 'generate-invoice-code': row.code }">
                  {{ row.value || "N.A" }}
                </dd>
              </div>
            </dl>
          </div>
          <div v-else class="generate-invoice-empty">
            Bank details show here once you choose an account.
          </div>
        </template>
      </v-card-text>

      <v-card-actions v-if="!$slots.prompt">
        <span v-if="loading" class="generate-invoice-status" role="status">
          Generating invoice, please wait...
        </span>
        <v-spacer></v-spacer>
        <v-btn
          class="oms-dialog-action--secondary"
          :disabled="loading"
          @click="$emit('cancel')"
          >No</v-btn
        >
        <v-btn
          v-permissions="permission"
          class="oms-dialog-action--primary"
          :loading="loading"
          :disabled="loading"
          @click="$emit('confirm')"
          >Yes</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
let uid = 0;

export default {
  name: "GenerateInvoiceDialog",
  model: {
    prop: "bank",
    event: "change",
  },
  props: {
    /* controls visibility */
    open: {
      type: Boolean,
      default: false,
    },
    /* v-model, the chosen bank's id */
    bank: {
      type: String,
      default: "",
    },
    /* Bank options as built by the callers' fetchBank():
       { text, value, beneficiaryName, branchCode, accountNo, ibanNo, swiftCode } */
    banks: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Generate Invoice",
    },
    /* Permission the Generate button needs, empty for none. */
    permission: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    uid += 1;
    return { fieldId: `generate-invoice-bank-${uid}` };
  },
  computed: {
    selected() {
      if (!this.bank) return null;
      return this.banks.find((item) => item.value === this.bank) || null;
    },
    details() {
      const bank = this.selected;
      return [
        { label: "Beneficiary name", value: bank.beneficiaryName, wide: true },
        { label: "Account number", value: bank.accountNo, code: true },
        { label: "SWIFT code", value: bank.swiftCode, code: true },
        { label: "IBAN", value: bank.ibanNo, code: true, wide: true },
        { label: "Branch", value: bank.branchCode, wide: true },
      ];
    },
  },
  methods: {
    select(value) {
      this.$emit("change", value);
    },
  },
};
</script>

<style>
/* Vuetify 1.5 sizes a dialog to its content, which made the popup jump in
   width between the empty, selected and credit duration states. */
.application .v-dialog.generate-invoice-dialog {
  width: min(34rem, calc(100vw - 2rem));
}

.application .v-dialog.generate-invoice-dialog .v-card__text {
  padding: 1.25rem 1.25rem 1.375rem !important;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-progress {
  margin: 0;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-progress .v-progress-linear__background,
.application .v-dialog.generate-invoice-dialog .generate-invoice-progress .v-progress-linear__bar__indeterminate .long,
.application .v-dialog.generate-invoice-dialog .generate-invoice-progress .v-progress-linear__bar__indeterminate .short {
  background-color: var(--oms-primary) !important;
}

.generate-invoice-dialog .generate-invoice-status {
  color: var(--oms-muted);
  font-size: 0.8125rem;
  font-weight: 600;
}

/* While generating, Yes keeps its colour and shows a spinner, instead of
   turning grey like an unavailable button. */
.application .v-dialog.generate-invoice-dialog .v-card__actions .v-btn.oms-dialog-action--primary.v-btn--loader {
  background: var(--oms-primary) !important;
  border-color: var(--oms-primary) !important;
  color: #ffffff !important;
  opacity: 1 !important;
}

.application .v-dialog.generate-invoice-dialog .v-btn--loader {
  position: relative;
}

.application .v-dialog.generate-invoice-dialog .v-btn--loader .v-btn__loading {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
}

.application .v-dialog.generate-invoice-dialog .v-btn--loader .v-progress-circular {
  color: #ffffff !important;
  height: 1.125rem !important;
  margin: 0 !important;
  width: 1.125rem !important;
}

.generate-invoice-dialog .generate-invoice-lead {
  color: var(--oms-ink-strong);
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 0.25rem;
}

.generate-invoice-dialog .generate-invoice-hint {
  color: var(--oms-muted);
  font-size: 0.8125rem;
  margin: 0 0 1.125rem;
}

.generate-invoice-dialog .generate-invoice-label {
  color: var(--oms-ink);
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.375rem;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field {
  margin: 0;
  padding: 0;
}

/* Boxed field: the shared dialog rules give the radius and focus ring but
   leave Vuetify's underline in place, so draw the border here instead. */
.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__slot {
  background: var(--oms-surface) !important;
  border: 0.0625rem solid var(--oms-control-border) !important;
  margin: 0;
  min-height: var(--oms-form-control-height);
  padding: 0 0.75rem !important;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__slot::before,
.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__slot::after {
  display: none;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__prepend-inner {
  align-self: center;
  margin-top: 0;
  padding-right: 0.5rem;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__prepend-inner .v-icon {
  color: var(--oms-muted) !important;
  font-size: 1.125rem;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-input__append-inner {
  align-self: center;
  margin-top: 0;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-select__selection,
.application .v-dialog.generate-invoice-dialog .generate-invoice-field input {
  color: var(--oms-ink-strong) !important;
  font-weight: 600;
}

.generate-invoice-dialog .generate-invoice-bank,
.generate-invoice-dialog .generate-invoice-empty {
  border-radius: 0.75rem;
  margin-top: 0.875rem;
}

.generate-invoice-dialog .generate-invoice-bank {
  background: var(--oms-surface-soft);
  border: 0.0625rem solid var(--oms-border);
  padding: 1rem 1.125rem;
}

.generate-invoice-dialog .generate-invoice-empty {
  border: 0.0625rem dashed var(--oms-border-strong);
  color: var(--oms-muted);
  font-size: 0.8125rem;
  padding: 1rem 1.125rem;
  text-align: center;
}

.generate-invoice-dialog .generate-invoice-details {
  display: grid;
  gap: 0.875rem 1.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
}

.generate-invoice-dialog .generate-invoice-detail--wide {
  grid-column: 1 / -1;
}

.generate-invoice-dialog .generate-invoice-detail dt {
  color: var(--oms-muted);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
}

.generate-invoice-dialog .generate-invoice-detail dd {
  color: var(--oms-ink-strong);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
  overflow-wrap: anywhere;
}

.generate-invoice-dialog .generate-invoice-code {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.generate-invoice-dialog .generate-invoice-credit-row {
  align-items: center;
  display: flex;
  gap: 0.625rem;
}

.generate-invoice-dialog .generate-invoice-credit-row .generate-invoice-field {
  flex: 1 1 auto;
  min-width: 0;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-credit-row .v-btn {
  flex: 0 0 auto;
  height: var(--oms-form-control-height) !important;
  margin: 0;
  min-width: 5.5rem !important;
}

.application .v-dialog.generate-invoice-dialog .generate-invoice-field .v-text-field__suffix {
  color: var(--oms-muted);
  font-size: 0.8125rem;
  padding-left: 0.5rem;
}

@media (max-width: 30rem) {
  .generate-invoice-dialog .generate-invoice-details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
